import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { PencilLine } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AgentArtifact } from '@hub/contracts'
import { ArtifactCard } from '@/components/assistant-ui/elements/artifact-card'
import { ImageLightbox } from './image-lightbox'

/**
 * The picture HUB looked at when it read a whiteboard (board f02h). The card
 * shows that exact picture, stored with the reply, not the board as it is now;
 * the overlay links on to the real board. The picture is an ordinary workspace
 * file, so the same project access rules apply to it. Only the newest pictures
 * of a board are kept and they go with the board, so an old card can outlive
 * its picture: it then says so and opens the board instead.
 */
export function WhiteboardReadCard({
  artifact,
  projectId,
  meta,
}: {
  artifact: AgentArtifact
  projectId: string
  meta: string
}) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)
  const [missing, setMissing] = useState(false)
  const navigate = useNavigate()
  const url = `/workspaces/${projectId}/${artifact.imagePath}`
  const title = artifact.title || t('artifacts.whiteboard')
  const activate = () => {
    if (missing) {
      void navigate({
        to: '/$projectId/draw/$drawingId',
        params: { projectId, drawingId: artifact.id },
      })
    } else {
      setOpen(true)
    }
  }

  return (
    <>
      <ArtifactCard
        title={title}
        meta={missing ? t('artifacts.whiteboardPictureGone') : meta}
        leading={
          missing ? undefined : (
            <img
              src={url}
              alt=""
              loading="lazy"
              onError={() => setMissing(true)}
              className="ring-foreground/10 h-12 w-16 shrink-0 rounded-lg bg-white object-cover ring-1"
            />
          )
        }
        onClick={activate}
        role={missing ? 'link' : 'button'}
        aria-label={missing ? t('citation.openWhiteboard') : t('lightbox.open', { name: title })}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            activate()
          }
        }}
      />
      {!missing && (
        <ImageLightbox
          image={{ url, filename: `${title}.png` }}
          caption={title}
          open={open}
          onOpenChange={setOpen}
          action={
            <Link
              to="/$projectId/draw/$drawingId"
              params={{ projectId, drawingId: artifact.id }}
              onClick={() => setOpen(false)}
              aria-label={t('citation.openWhiteboard')}
              title={t('citation.openWhiteboard')}
              className="flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              <PencilLine className="size-4" />
              <span className="hidden sm:inline">{t('citation.openWhiteboard')}</span>
            </Link>
          }
        />
      )}
    </>
  )
}
